'use client';

import { useAuthState } from '@/app/_providers/store';
import { toaster } from '@/components/ui';
import BarLoader from '@/components/ui/loaders/BarLoader';
import useAuthFunc from '@/hooks/auth/useAuthFunc';
import { partnerPackagePaymentRequest } from '@/services/auth/auth.service';
import { Box, Button, Field, Input, Stack, Text, Textarea } from '@chakra-ui/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CanLogoIcon } from '../../../../../../public/assets/icons';
import DiscountModalPackage from './discount/createDiscountModal';
import { TPackageDiscount } from './discount/packageDiscount.validator';
import { PackageFormValues } from './types/template.types';

const PartnerPackageRequest = () => {
   const {
      register,
      handleSubmit,
      formState: { errors },
   } = useForm<PackageFormValues>();
   const { user } = useAuthState();
   const { refreshSession, redirect } = useAuthFunc();

   const [openDiscountCreateModal, setOpenDiscountModal] = useState<boolean>(false);
   const [discounts, setDiscounts] = useState<TPackageDiscount[]>([]);
   const [isLoading, setIsLoading] = useState<boolean>(false);

   const onSubmit = handleSubmit(async data => {
      setIsLoading(true);
      const [error, response] = await partnerPackagePaymentRequest({
         ...data,
         PartnerpackageDiscount: discounts,
         user,
      });
      if (error) {
         console.log(error);
         setIsLoading(false);
         return toaster.error({ title: error.message });
      }
      await refreshSession();

      if (response) {
         toaster.success({ title: response.message });
      }

      redirect('COMPLETED');
      setIsLoading(false);
   });

   if (!user) {
      return;
   }

   const removeDiscount = (index: number) => {
      const updated = [...discounts];
      updated.splice(index, 1);
      setDiscounts(updated);
   };

   return (
      <>
         {isLoading && <BarLoader loadingText="Verifying" />}
         <Box minH="72px">
            <CanLogoIcon />
         </Box>
         <Text
            as="h2"
            color="white"
            fontSize={{ base: '4xl', sm: '5xl', md: '12xl' }}
            fontWeight="bold"
            textAlign="center"
         >
            PACKAGE SELECTION
         </Text>
         <Text color="white" textStyle="md" textAlign="center" mt="-10px" mb="2">
            We are really pleased that you&apos;re looking to join us as a partner/collaborator. Please let us know the
            following information and we will be in touch within 24 hours:
         </Text>

         <form onSubmit={onSubmit}>
            <Stack gap="4" align="flex-start" maxW="sm">
               <Field.Root invalid={!!errors.company}>
                  <Field.Label>Name of company</Field.Label>
                  <Input placeholder="Name of company" {...register('company', { required: 'company is required' })} />
                  <Field.ErrorText>{errors.company?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={!!errors.description}>
                  <Field.Label>What do you do?</Field.Label>
                  <Textarea
                     placeholder="What do you do?"
                     {...register('description', { required: 'description is required' })}
                  />
                  <Field.HelperText>A short description of yourself</Field.HelperText>
                  <Field.ErrorText>{errors.description?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={!!errors.description}>
                  <Field.Label>
                     {' '}
                     What are you offering to the Cancard community ie. discount/exclusive access etc.
                  </Field.Label>

                  {discounts.length > 0 && (
                     <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                        {discounts.map((item, index) => (
                           <Box
                              key={index}
                              display="flex"
                              alignItems="center"
                              gap={2}
                              bg="gray.100"
                              px={3}
                              py={1}
                              borderRadius="md"
                           >
                              <Text fontSize="sm" color="black">
                                 {item.code}
                              </Text>

                              <Button
                                 style={{
                                    padding: '12px 5px',
                                    lineHeight: '0px',
                                    height: '20px',
                                    fontSize: '15px',
                                    width: 'auto',
                                    margin: '0px',
                                    minWidth: 'auto',
                                    borderRadius: '5px',
                                 }}
                                 size="xs"
                                 variant="ghost"
                                 color="red.500"
                                 onClick={() => removeDiscount(index)}
                              >
                                 ✕
                              </Button>
                           </Box>
                        ))}
                     </Box>
                  )}

                  <Button
                     // style={{
                     //    height: '34px',
                     //    fontSize: '13px',
                     //    marginTop: '10px',
                     // }}
                     size="xs"
                     bg="#f5e876.500"
                     color="black"
                     _hover={{ bg: '#f5e876.600' }}
                     borderRadius="md"
                     px={2}
                     py={3}
                     onClick={() => setOpenDiscountModal(true)}
                     style={{
                        minHeight: 'auto',
                        padding: '7px 10px',
                        height: 'auto',
                     }}
                  >
                     {discounts.length ? 'Add more discount!' : 'Add discount'}
                  </Button>
               </Field.Root>

               <Box
                  style={{
                     width: '100%',
                     textAlign: 'center',
                     marginTop: '40px',
                  }}
               >
                  <Button
                     type="submit"
                     disabled={!discounts.length}
                     style={{
                        minHeight: 'auto',
                        padding: '7px 15px',
                        height: 'auto',
                        borderRadius: '5px',
                     }}
                  >
                     Submit
                  </Button>
               </Box>
            </Stack>
         </form>
         {openDiscountCreateModal && (
            <DiscountModalPackage
               isOpen={openDiscountCreateModal}
               onClose={() => setOpenDiscountModal(false)}
               setDiscounts={setDiscounts}
            />
         )}
      </>
   );
};

export default PartnerPackageRequest;
